import React, { useEffect, useState } from 'react';
import { getInvoiceByShareToken } from '../lib/db';
import { formatMoney, formatDate } from '../lib/formatters';
import { printInvoice } from '../lib/printInvoice';

const STATUS_COLORS = {
  draft:   'bg-slate-100 text-slate-600',
  sent:    'bg-blue-100 text-blue-700',
  viewed:  'bg-indigo-100 text-indigo-700',
  partial: 'bg-amber-100 text-amber-700',
  paid:    'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  void:    'bg-slate-100 text-slate-400',
};

export default function InvoicePublicPage({ token }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    getInvoiceByShareToken(token)
      .then(({ data, error: err }) => {
        if (err || !data) { setError('Invoice not found or this link has expired.'); }
        else { setInvoice(data); }
      })
      .catch(() => setError('Could not load invoice. Please try again.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-slate-500 text-sm">Loading invoice…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center space-y-3 max-w-sm">
        <div className="text-4xl">📄</div>
        <h2 className="text-lg font-semibold text-slate-800">Invoice not found</h2>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    </div>
  );

  const { firm, client, line_items = [], ...inv } = invoice;
  const handleDownload = () => printInvoice({ ...inv, firm, client, line_items });
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ')
    || client?.organization || '—';

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      {/* Floating download button */}
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={handleDownload}
        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-lg transition-colors">
        ⬇ Download PDF
      </button>
    </div>

    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">

        {/* Header */}
        <div className="bg-indigo-700 px-8 py-8 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              {firm?.logo_url && (
                <img
                  src={firm.logo_url}
                  alt={firm?.name || 'Firm logo'}
                  className="h-14 w-auto max-w-[180px] object-contain bg-white rounded-lg p-1.5 flex-shrink-0"
                />
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight truncate">{firm?.name || 'Invoice'}</h1>
                {firm?.address && <p className="text-indigo-200 text-sm mt-1">{firm.address}</p>}
                {firm?.email  && <p className="text-indigo-200 text-sm">{firm.email}</p>}
                {firm?.phone  && <p className="text-indigo-200 text-sm">{firm.phone}</p>}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-indigo-200 text-sm uppercase tracking-widest">Invoice</p>
              <p className="text-xl font-bold mt-0.5">{inv.invoice_number}</p>
              <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_COLORS[inv.status] || 'bg-slate-100 text-slate-600'}`}>
                {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
          {[
            { label: 'Billed to', value: clientName },
            { label: 'Issue date', value: formatDate(inv.issue_date) },
            { label: 'Due date',   value: formatDate(inv.due_date) },
          ].map(({ label, value }) => (
            <div key={label} className="px-6 py-4">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</p>
              <p className="text-sm font-medium text-slate-800">{value}</p>
            </div>
          ))}
        </div>

        {/* Line items */}
        <div className="px-8 py-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="text-left pb-3 font-medium">Description</th>
                <th className="text-right pb-3 font-medium pl-8 w-20">Qty</th>
                <th className="text-right pb-3 font-medium pl-8 w-36">Rate</th>
                <th className="text-right pb-3 font-medium pl-8 w-36">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {line_items.length > 0 ? line_items.map((li, i) => (
                <tr key={i}>
                  <td className="py-3 text-slate-700">{li.description}</td>
                  <td className="py-3 pl-8 text-right text-slate-500 font-mono">{li.quantity}</td>
                  <td className="py-3 pl-8 text-right text-slate-500 font-mono">{formatMoney(li.rate)}</td>
                  <td className="py-3 pl-8 text-right font-medium text-slate-800 font-mono">{formatMoney(li.amount)}</td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="py-6 text-center text-slate-400">No line items</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end px-8 pb-8">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-500">
              <span>Subtotal</span>
              <span className="font-mono">{formatMoney(inv.subtotal || 0)}</span>
            </div>
            {Number(inv.discount_amount) > 0 && (
              <div className="flex justify-between text-slate-500">
                <span>Discount</span>
                <span className="font-mono text-emerald-600">−{formatMoney(inv.discount_amount)}</span>
              </div>
            )}
            {Number(inv.tax_amount) > 0 && (
              <div className="flex justify-between text-slate-500">
                <span>Tax ({inv.tax_rate}%)</span>
                <span className="font-mono">{formatMoney(inv.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-2 mt-1">
              <span>Total</span>
              <span className="font-mono">{formatMoney(inv.total || 0)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {inv.notes && (
          <div className="px-8 pb-8">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Notes</p>
              <p className="text-sm text-slate-600 whitespace-pre-line">{inv.notes}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-slate-100 px-8 py-4 text-center text-xs text-slate-400">
          This invoice was generated by {firm?.name || 'your legal firm'}. Please contact them for any queries.
        </div>
      </div>
    </div>
  );
}
